/**
 * useAdminBookings Hook
 *
 * Provides admin booking management with filtering and pagination
 * using real Firestore queries.
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export interface AdminBooking {
  id: string;
  listingId: string;
  listingTitle: string;
  listingPhoto: string;
  listingCity: string;
  guestId: string;
  guestName: string;
  guestEmail: string;
  guestPhone?: string;
  hostId: string;
  hostName: string;
  hostEmail: string;
  checkIn: Date;
  checkOut: Date;
  nights: number;
  guests: { adults: number; children: number };
  status: 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled';
  paymentStatus: 'pending' | 'paid' | 'refunded' | 'partial_refund';
  pricing: {
    nightlyRate: number;
    nightlyTotal: number;
    cleaningFee: number;
    platformCommission: number;
    totalAmount: number;
    hostPayout: number;
  };
  createdAt: Date;
  confirmedAt?: Date;
  cancelledAt?: Date;
  cancelledBy?: 'guest' | 'host' | 'admin';
  cancellationReason?: string;
}

export interface AdminBookingsFilter {
  search?: string;
  status?: 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled' | '';
  dateFrom?: string;
  dateTo?: string;
  hostId?: string;
  guestId?: string;
  listingId?: string;
  sortBy?: 'createdAt' | 'checkIn' | 'totalAmount';
  sortOrder?: 'asc' | 'desc';
}

export interface BookingsStats {
  total: number;
  pending: number;
  confirmed: number;
  active: number;
  completed: number;
  cancelled: number;
}

const PAGE_SIZE = 10;

const toDate = (val: any): Date => {
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return val ? new Date(val) : new Date();
};

export const useAdminBookings = (filter: AdminBookingsFilter, page: number = 0) => {
  const [bookings, setBookings] = useState<AdminBooking[]>([]);
  const [stats, setStats] = useState<BookingsStats>({ total: 0, pending: 0, confirmed: 0, active: 0, completed: 0, cancelled: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchBookings = async () => {
      setIsLoading(true);
      try {
        const [bookingsSnap, usersSnap, listingsSnap] = await Promise.all([
          getDocs(query(collection(db, 'bookings'), orderBy('createdAt', 'desc'))),
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'listings')),
        ]);

        // Build lookup maps
        const userMap = new Map<string, { name: string; email: string; phone?: string }>();
        usersSnap.docs.forEach((d) => {
          const data = d.data();
          userMap.set(d.id, {
            name: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            email: data.email || '',
            phone: data.phone,
          });
        });

        const listingMap = new Map<string, { title: string; photo: string; city: string }>();
        listingsSnap.docs.forEach((d) => {
          const data = d.data();
          listingMap.set(d.id, {
            title: data.title || 'Untitled',
            photo: data.photos?.[0]?.url || '',
            city: data.location?.city || '',
          });
        });

        let allBookings: AdminBooking[] = bookingsSnap.docs.map((docSnap) => {
          const data = docSnap.data();
          const guest = userMap.get(data.guestId) || { name: 'Unknown', email: '' };
          const host = userMap.get(data.hostId) || { name: 'Unknown', email: '' };
          const listing = listingMap.get(data.listingId) || { title: 'Unknown', photo: '', city: '' };
          const snapshot = data.listingSnapshot;

          return {
            id: docSnap.id,
            listingId: data.listingId || '',
            listingTitle: snapshot?.title || listing.title,
            listingPhoto: snapshot?.photoUrl || listing.photo,
            listingCity: snapshot?.address?.split(',')[0] || listing.city,
            guestId: data.guestId || '',
            guestName: guest.name,
            guestEmail: guest.email,
            guestPhone: guest.phone,
            hostId: data.hostId || '',
            hostName: host.name,
            hostEmail: host.email,
            checkIn: toDate(data.checkIn),
            checkOut: toDate(data.checkOut),
            nights: data.nights || 0,
            guests: { adults: data.adults || 1, children: data.children || 0 },
            status: data.status || 'pending',
            paymentStatus: data.paymentStatus || 'pending',
            pricing: {
              nightlyRate: data.pricing?.nightlyTotal ? (data.pricing.nightlyTotal / (data.nights || 1)) : 0,
              nightlyTotal: data.pricing?.nightlyTotal || 0,
              cleaningFee: data.pricing?.cleaningFee || 0,
              platformCommission: data.pricing?.platformCommission || 0,
              totalAmount: data.pricing?.totalAmount || 0,
              hostPayout: data.pricing?.hostPayout || 0,
            },
            createdAt: toDate(data.createdAt),
            confirmedAt: data.confirmedAt ? toDate(data.confirmedAt) : undefined,
            cancelledAt: data.cancelledAt ? toDate(data.cancelledAt) : undefined,
            cancelledBy: data.cancelledBy,
            cancellationReason: data.cancellationReason,
          };
        });

        // Calculate stats from full dataset
        const bookingsStats: BookingsStats = {
          total: allBookings.length,
          pending: allBookings.filter((b) => b.status === 'pending').length,
          confirmed: allBookings.filter((b) => b.status === 'confirmed').length,
          active: allBookings.filter((b) => b.status === 'active').length,
          completed: allBookings.filter((b) => b.status === 'completed').length,
          cancelled: allBookings.filter((b) => b.status === 'cancelled').length,
        };

        // Apply filters
        if (filter.search) {
          const searchLower = filter.search.toLowerCase();
          allBookings = allBookings.filter(
            (b) =>
              b.id.toLowerCase().includes(searchLower) ||
              b.guestName.toLowerCase().includes(searchLower) ||
              b.hostName.toLowerCase().includes(searchLower) ||
              b.listingTitle.toLowerCase().includes(searchLower)
          );
        }

        if (filter.status) {
          allBookings = allBookings.filter((b) => b.status === filter.status);
        }

        if (filter.dateFrom) {
          const from = new Date(filter.dateFrom);
          allBookings = allBookings.filter((b) => b.checkIn >= from);
        }
        if (filter.dateTo) {
          const to = new Date(filter.dateTo);
          allBookings = allBookings.filter((b) => b.checkIn <= to);
        }

        if (filter.hostId) allBookings = allBookings.filter((b) => b.hostId === filter.hostId);
        if (filter.guestId) allBookings = allBookings.filter((b) => b.guestId === filter.guestId);
        if (filter.listingId) allBookings = allBookings.filter((b) => b.listingId === filter.listingId);

        // Sort
        const sortBy = filter.sortBy || 'createdAt';
        const sortOrder = filter.sortOrder || 'desc';
        allBookings.sort((a, b) => {
          let aVal: number, bVal: number;
          if (sortBy === 'checkIn') { aVal = a.checkIn.getTime(); bVal = b.checkIn.getTime(); }
          else if (sortBy === 'totalAmount') { aVal = a.pricing.totalAmount; bVal = b.pricing.totalAmount; }
          else { aVal = a.createdAt.getTime(); bVal = b.createdAt.getTime(); }
          return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });

        if (!cancelled) {
          setStats(bookingsStats);
          setTotalCount(allBookings.length);
          const start = page * PAGE_SIZE;
          setBookings(allBookings.slice(start, start + PAGE_SIZE));
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Failed to fetch bookings:', err);
        if (!cancelled) {
          setBookings([]);
          setTotalCount(0);
          setIsLoading(false);
        }
      }
    };

    fetchBookings();
    return () => { cancelled = true; };
  }, [filter.search, filter.status, filter.dateFrom, filter.dateTo, filter.hostId, filter.guestId, filter.listingId, filter.sortBy, filter.sortOrder, page]);

  return {
    bookings,
    stats,
    totalCount,
    isLoading,
    hasMore: (page + 1) * PAGE_SIZE < totalCount,
    totalPages: Math.ceil(totalCount / PAGE_SIZE),
  };
};

export const useAdminBooking = (bookingId: string) => {
  const [booking, setBooking] = useState<AdminBooking | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchBooking = async () => {
      setIsLoading(true);
      try {
        const docSnap = await getDoc(doc(db, 'bookings', bookingId));
        if (!cancelled && docSnap.exists()) {
          const data = docSnap.data();

          // Fetch related data
          const [guestSnap, hostSnap, listingSnap] = await Promise.all([
            data.guestId ? getDoc(doc(db, 'users', data.guestId)) : null,
            data.hostId ? getDoc(doc(db, 'users', data.hostId)) : null,
            data.listingId ? getDoc(doc(db, 'listings', data.listingId)) : null,
          ]);

          const guestData = guestSnap?.data();
          const hostData = hostSnap?.data();
          const listingData = listingSnap?.data();
          const snapshot = data.listingSnapshot;

          setBooking({
            id: docSnap.id,
            listingId: data.listingId || '',
            listingTitle: snapshot?.title || listingData?.title || 'Unknown',
            listingPhoto: snapshot?.photoUrl || listingData?.photos?.[0]?.url || '',
            listingCity: listingData?.location?.city || '',
            guestId: data.guestId || '',
            guestName: guestData?.displayName || 'Unknown',
            guestEmail: guestData?.email || '',
            guestPhone: guestData?.phone,
            hostId: data.hostId || '',
            hostName: hostData?.displayName || 'Unknown',
            hostEmail: hostData?.email || '',
            checkIn: toDate(data.checkIn),
            checkOut: toDate(data.checkOut),
            nights: data.nights || 0,
            guests: { adults: data.adults || 1, children: data.children || 0 },
            status: data.status || 'pending',
            paymentStatus: data.paymentStatus || 'pending',
            pricing: {
              nightlyRate: data.pricing?.nightlyTotal ? (data.pricing.nightlyTotal / (data.nights || 1)) : 0,
              nightlyTotal: data.pricing?.nightlyTotal || 0,
              cleaningFee: data.pricing?.cleaningFee || 0,
              platformCommission: data.pricing?.platformCommission || 0,
              totalAmount: data.pricing?.totalAmount || 0,
              hostPayout: data.pricing?.hostPayout || 0,
            },
            createdAt: toDate(data.createdAt),
            confirmedAt: data.confirmedAt ? toDate(data.confirmedAt) : undefined,
            cancelledAt: data.cancelledAt ? toDate(data.cancelledAt) : undefined,
            cancelledBy: data.cancelledBy,
            cancellationReason: data.cancellationReason,
          });
        } else if (!cancelled) {
          setBooking(null);
        }
        if (!cancelled) setIsLoading(false);
      } catch (err) {
        console.error('Failed to fetch booking:', err);
        if (!cancelled) { setBooking(null); setIsLoading(false); }
      }
    };

    if (bookingId) fetchBooking();
    return () => { cancelled = true; };
  }, [bookingId]);

  return { booking, isLoading };
};

export default useAdminBookings;
